// Lists: collections of messages. A channel's pins are one kind of list (one per channel or DM); anyone can also
// make named lists that gather messages from across channels, shared with the server or private to themselves.
//
// The lists and their items live in the server's room (worker/src/lists.ts). A message carries the ids of the
// shared lists it's in (`Message.lists`, pins included), so everyone who can see it sees its badges; private list
// membership is only ever sent to its owner (`ready.privateItems`, "listItem" events on their own sockets).
//
// Naming: people see named lists as "collections" (sidebar "Collections", "Add to collection…") and pins as "Pinned".
// Code, tables, the protocol and the iOS app keep "list" (`lists`, `list_items`, `Message.lists`, `listItem`): only
// words people read say "collection".

import { cleanName } from "./cleanName.ts";

/** "pins": a channel's (or DM's) pinned messages. "custom": a named list. */
export type ListKind = "pins" | "custom";
/** "server": everyone in the server sees it (who may add: `addPolicy`). "private": only its owner. Pins are always "server". */
export type ListVisibility = "server" | "private";

export interface ListInfo {
  id: string;
  kind: ListKind;
  visibility: ListVisibility;
  /** Pins only: the channel or DM they belong to. */
  channelId?: string;
  /** Private lists only: whose it is. */
  ownerId?: string;
  /** "" for pins (clients call them "Pinned"). */
  name: string;
  /** One emoji, or "" (clients draw a plain list icon). */
  emoji: string;
  createdBy: string;
  createdAt: number;
  /** Server time of the newest change to the list or its items. */
  updatedAt: number;
  /** Messages in it (that the reader can see). */
  count: number;
  /**
   * Shared lists only: who may add to it. "everyone" (the default) or "chosen": its creator, the people in
   * `adders`, and owners and admins. Set by whoever may rename the list.
   */
  addPolicy?: ListAddPolicy;
  /** With addPolicy "chosen": the other people who may add (user ids). */
  adders?: string[];
}

export type ListAddPolicy = "everyone" | "chosen";
/** People a locked list can name as adders. */
export const MAX_LIST_ADDERS = 50;

/**
 * Who's acting on a list. People and AIs alike: an AI acting for someone (@Bloom, the connector) passes that
 * person's id and role, so it can never do more than they could. These checks are the only list rules; the room
 * enforces them and clients use them to decide what to offer.
 */
export interface ListActor {
  userId: string;
  role: "owner" | "admin" | "member";
}

type ListRules = Pick<ListInfo, "kind" | "visibility" | "ownerId" | "createdBy" | "addPolicy" | "adders">;
const moderates = (a: ListActor) => a.role === "owner" || a.role === "admin";

/** May `a` put messages in this list (seeing the message and the list are checked separately)? */
export function canAddTo(l: ListRules, a: ListActor): boolean {
  if (l.visibility === "private") return l.ownerId === a.userId;
  if (l.kind === "pins" || l.addPolicy !== "chosen") return true;
  return l.createdBy === a.userId || moderates(a) || !!l.adders?.includes(a.userId);
}

/**
 * May `a` take out a message that `addedBy` put in? Whoever added it, the list's creator, or an owner or admin.
 * Pins have no creator: in a channel it's whoever pinned it or a moderator; in a DM any of its members.
 */
export function canRemoveFrom(l: ListRules, addedBy: string, a: ListActor, inDm: boolean): boolean {
  if (l.visibility === "private") return l.ownerId === a.userId;
  if (addedBy === a.userId || moderates(a)) return true;
  if (l.kind === "pins") return inDm;
  return l.createdBy === a.userId;
}

/** May `a` rename, delete or lock this list? Its creator, or an owner or admin. Never pins. */
export function canManageList(l: ListRules, a: ListActor): boolean {
  if (l.kind === "pins") return false;
  if (l.visibility === "private") return l.ownerId === a.userId;
  return l.createdBy === a.userId || moderates(a);
}

/** One message in a list. */
export interface ListItem {
  listId: string;
  messageId: string;
  /** The message's stream: a channel, DM or thread ("t:<parent id>"). */
  channelId: string;
  /** The channel or DM that stream belongs to (itself, or a thread's channel). */
  rootId: string;
  addedBy: string;
  addedAt: number;
}

/**
 * A quiet line in the conversation saying someone pinned a message or added it to shared lists ("Sam pinned a
 * message and added it to 🍝 Recipes"). Stored as a message (authorId: who did it) so it's in history and in order.
 * Several adds of one message by one person in a few minutes share one line. Private lists never show up here.
 */
export interface SystemEvent {
  kind: "listAdd";
  /** The message that was added (in the same stream as this line). */
  messageId: string;
  /** Shared list ids, pins included, in the order they were added. */
  lists: string[];
}

/** Clients send this instead of a list id to mean "the pins of the message's own channel". */
export const PINS = "pins";
export const MAX_LIST_NAME = 40;
/** Shared lists per server, and private lists per person. */
export const MAX_LISTS = 200;
export const MAX_PRIVATE_LISTS = 100;
/** Messages per list (pins included). */
export const MAX_LIST_ITEMS = 1000;
/** List items per "listItems" page. */
export const LIST_PAGE = 50;
/** A second add of the same message by the same person within this long joins the first one's line. */
export const LIST_LINE_MERGE_MS = 10 * 60_000;

/** The label a list goes by: "Pinned" for pins, else its name. */
export function listLabel(l: Pick<ListInfo, "kind" | "name">): string {
  return l.kind === "pins" ? "Pinned" : l.name;
}

/** A list name as the room keeps it (`cleanName`: no direction or zero-width controls, one space between words, trimmed, at most MAX_LIST_NAME); "" if nothing's left. */
export function cleanListName(raw: unknown): string {
  return cleanName(raw, MAX_LIST_NAME);
}

/** One emoji (it may be several code points), or "" for none / anything that isn't one. */
export function cleanListEmoji(raw: unknown): string {
  const e = String(raw ?? "").trim();
  if (!e || e.length > 32) return "";
  // A pictograph, a flag, or a keycap (1️⃣, #️⃣: a digit, # or * then U+20E3).
  if (!/\p{Extended_Pictographic}|\p{Regional_Indicator}|[0-9#*]️?⃣/u.test(e)) return "";
  // Nothing but emoji pieces.
  if (/[\p{L}\p{N}\s]/u.test(e.replace(/\p{Extended_Pictographic}|\p{Emoji_Component}|‍|️|⃣/gu, ""))) return "";
  return e;
}
